import { ReactNode, memo, KeyboardEvent } from 'react';
import { UseFormRegister } from 'react-hook-form';
import { getValueByNestedKey } from '@utils/utils';
import TableHeaderSelect from '../../TableHeaderSelect/ui/TableHeaderSelect';
import TableRowCell from '../../TableRowCell/ui/TableRowCell';
import styles from './TableRow.module.scss';

interface ITableRowProps {
  data: any;
  columns: any[];
  rowActions: (classname: string, data?: any) => ReactNode;
  rowHeight: string;
  roundLastRow?: boolean;
  register?: UseFormRegister<any>;
  onClick?: (data: any) => void;
  selectable?: boolean;
  selectedRows?: string[];
}

/**
 * @param props - The props for the component.
 * @param columns - The columns array.
 * @param data - The table data
 * @param rowActions - Actions which is called if the user click to the row
 * @param roundLastRow - Indicates whether last row should be rounded
 * @param rowHeight - The row height
 * @param register - the path name to the form field value, name is required and unique
 * @param onClick - Function which is called after clicking table row
 * @returns A React component that displays the tab row.
 */

function TableRowComponent(props: ITableRowProps) {
  const {
    data,
    columns,
    rowActions,
    register,
    selectable,
    rowHeight,
    onClick,
    selectedRows,
    roundLastRow = false,
  } = props;
  const selected = selectable ? selectedRows?.includes(data.id) : false;

  const handleClick = () => {
    if (onClick) {
      onClick(data);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(data);
    }
  };

  const interactiveProps = onClick
    ? {
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        role: 'button' as const,
        tabIndex: 0,
      }
    : {};

  return (
    <div
      className={[
        styles.tableRow,
        onClick && styles.clickable,
        selected && styles.selected,
      ].join(' ')}
      style={{
        minHeight: rowHeight === 'auto' ? '60px' : undefined,
        height: rowHeight,
        borderBottomLeftRadius: roundLastRow ? '8px' : undefined,
        borderBottomRightRadius: roundLastRow ? '8px' : undefined,
      }}
      {...interactiveProps}
    >
      {selectable && (
        <TableHeaderSelect selected={selectedRows?.includes(data.id)} />
      )}
      {columns.map((item) => {
        return (
          <TableRowCell
            className={onClick && styles.clickableRow}
            key={`${data.id}_${item.key}`}
            columnType={item.type}
            data={data}
            value={getValueByNestedKey(data, item.key)}
            actions={item.key === 'actions'}
            rowActions={rowActions}
            id={data.id}
            register={register}
            keyName={item.key}
            {...item}
          />
        );
      })}
    </div>
  );
}

const TableRow = memo(TableRowComponent);

export default TableRow;
